import { useEffect, useMemo, useRef, useState } from 'react';
import { BarChartUrl } from '@icons';
import { ClinicalData } from '@/api/apis/case';
import { BloodGasRecord } from '../components/BloodGasRecord';
import { ClinicalSection } from '../components';
import { formatBloodGases } from './utils';

export const BloodGases = ({ clinicalData }: { clinicalData: ClinicalData }) => {
  const bloodGases = useMemo(() => formatBloodGases(clinicalData), [clinicalData]);
  const [selectedIndex, setSelectedIndex] = useState(bloodGases.length - 1);
  const listContainerRef = useRef<HTMLDivElement>(null);

  const activeIndex = selectedIndex >= 0 && selectedIndex < bloodGases.length ? selectedIndex : bloodGases.length - 1;
  const selectedBloodGas = bloodGases[activeIndex];

  useEffect(() => {
    if (listContainerRef?.current) {
      listContainerRef.current.scrollBy({ left: listContainerRef.current.scrollWidth });
    }
  }, [bloodGases, listContainerRef]);

  const max = useMemo(
    () => Math.max(...bloodGases.map((bg) => bg.paO2 ?? 0), ...bloodGases.map((bg) => bg.pvO2 ?? 0)),
    [bloodGases],
  );

  return (
    <ClinicalSection className="row-span-1" color="bg-blue-40" header="Assessment Blood Gases" icon={BarChartUrl}>
      {bloodGases.length > 0 && (
        <div className="flex flex-col gap-4 w-full">
          {selectedBloodGas && (
            <div className="flex justify-between items-center w-full">
              <div className="text-hs">
                PF Ratio <span className="text-xl-sb">{selectedBloodGas.pfRatio}</span>
                {selectedBloodGas.isOnOcs ? ` at ${selectedBloodGas.ocsTime} PT, ` : ' at '}
                {selectedBloodGas.time}
              </div>
              <div className="flex gap-6">
                <div className="flex gap-1 items-center">
                  <div className="w-1.5 h-1.5 bg-blue-saturated rounded-full" />
                  <div className="text-blue-saturated text-sm">PvO2: {selectedBloodGas.pvO2} mmHg</div>
                </div>
                <div className="flex gap-1 items-center">
                  <div className="w-1.5 h-1.5 bg-maroon-20 rounded-full" />
                  <div className="text-maroon-20 text-sm">PaO2: {selectedBloodGas.paO2} mmHg</div>
                </div>
              </div>
            </div>
          )}
          <div ref={listContainerRef} className="flex gap-4 items-end w-full overflow-x-auto scroll">
            {bloodGases.map((item, index) => (
              <BloodGasRecord
                key={item.timestamp}
                isSelected={index === activeIndex}
                onClick={() => setSelectedIndex(index)}
                record={item}
                maxValue={max}
              />
            ))}
          </div>
        </div>
      )}
    </ClinicalSection>
  );
};
